import {
  ActionButton,
  ActionsGroup,
  Button,
  Col,
  ExtraInfo,
  Modal,
  Panel,
  Row,
  SectionTitle,
  Table
} from '@elotech/components';
import {
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';

import { alert } from '../alert';
import { SILENT_REJECTION } from '../error/ErrorBoundary';
import { TableContainer } from '../table';
import { getFileName, getUnaccentFileName } from './arquivoUtils';

type ArquivoPropsContext = {
  file: File;
  patch: any;
  initial: any;
  isNew: boolean;
  fileKey: string;
  updateFile: (patch: any) => void;
  setValidFile: (isValid: boolean) => void;
};

export const ArquivoPropsContextInner = createContext<ArquivoPropsContext>(
  {} as ArquivoPropsContext
);

export const useArquivoPropsContext = () =>
  useContext(ArquivoPropsContextInner);

export type ArquivoPropsComponentRefType = {
  addFile(file: File, initial: any, isNew: boolean): Promise<any>;
};

type Props = {
  children?: ReactNode;
  showArquivoProps: boolean;
  renderDragDrop: () => ReactNode;
  labels?(file): any;
};

type FileResolver = {
  key: string;
  index: number;
  file: File;
  resolve(value: any): void;
  reject(reason: any): void;
  patch: any;
  initial: any;
  isValid: boolean;
  isNew: boolean;
};

export const initialArquivoPropsComponentRef = {
  addFile() {
    return Promise.resolve();
  }
};
export const ArquivoPropsComponent = forwardRef<
  ArquivoPropsComponentRefType,
  Props
>(({ children, showArquivoProps, renderDragDrop, labels }, ref) => {
  if (!children || !showArquivoProps) {
    useImperativeHandle(ref, () => initialArquivoPropsComponentRef);

    return null;
  }

  const [files, setFiles] = useState<{
    [key: string]: FileResolver;
  }>({});

  const [showingMessage, setShowingMessage] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');
  const filesValues = useMemo(() => Object.values(files), [files]);
  const filesKeys = useMemo(() => Object.keys(files), [files]);

  const dragDropComponent = useMemo(() => renderDragDrop(), [renderDragDrop]);

  useEffect(() => {
    if (!filesKeys.length || filesKeys.includes(selectedFile)) {
      return;
    }
    setSelectedFile(filesKeys[0]);
  }, [filesKeys, selectedFile]);

  useImperativeHandle(ref, () => ({
    addFile(file: File, initial: any, isNew: boolean) {
      if (
        filesValues.some(
          f => f.file.name === file.name && f.file.size === file.size
        )
      ) {
        return Promise.reject({
          message: `O arquivo ${file.name} já foi selecionado`
        });
      }

      return new Promise<any>((resolve, reject) => {
        const key = `file-${Math.random()}-${file.name}`;

        setFiles(oldFiles => {
          const entity = {
            key,
            index: -1,
            file,
            resolve,
            reject,
            isValid: true,
            initial,
            patch: {},
            isNew
          };
          oldFiles[key] = entity;

          oldFiles[key].index = Object.keys(oldFiles).length;

          return { ...oldFiles };
        });
        setSelectedFile(key);
      });
    }
  }));

  const removeFile = (key: string) => {
    setFiles(oldFiles => {
      const file = oldFiles[key];
      if (!file) {
        return oldFiles;
      }
      file.reject(SILENT_REJECTION);
      delete oldFiles[key];
      return { ...oldFiles };
    });
  };

  const previewFile = (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    window.open(fileUrl, '_blank', 'noopener noreferrer');
    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 10000);
  };

  const askToClose = () => {
    setShowingMessage(true);
    alert({
      type: 'question',
      title:
        'Se sair, irá cancelar o envio dos arquivos. Tem certeza que deseja cancelar?',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
      onConfirm() {
        filesKeys.forEach(removeFile);
      }
    }).finally(() => setShowingMessage(false));
  };

  const visible = !showingMessage && filesKeys.length > 0;

  useEffect(() => {
    if (visible) {
      document.getElementById('save-files-button')?.focus();
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  const renderFile = (fileResolver: FileResolver) => {
    const { file, key } = fileResolver;

    const updateFile = (patch: any) => {
      setFiles(oldFiles => {
        const newFile = oldFiles[key];
        if (
          !newFile ||
          JSON.stringify(patch) === JSON.stringify(newFile.patch)
        ) {
          return oldFiles;
        }
        newFile.patch = patch;
        return { ...oldFiles };
      });
    };

    const setValidFile = (isValid: boolean) => {
      setFiles(oldFiles => {
        const newFile = oldFiles[key];
        if (!newFile || newFile.isValid === isValid) {
          return oldFiles;
        }
        newFile.isValid = isValid;
        return { ...oldFiles };
      });
    };

    return (
      <Col
        sm={7}
        md={7}
        key={key}
        style={{
          display: key === selectedFile ? 'block' : 'none'
        }}
      >
        <SectionTitle>{getFileName(fileResolver.patch, file)}</SectionTitle>
        <ArquivoPropsContextInner.Provider
          value={{
            file,
            updateFile,
            setValidFile,
            initial: fileResolver.initial,
            isNew: fileResolver.isNew,
            patch: fileResolver.patch,
            fileKey: key
          }}
        >
          {children}
        </ArquivoPropsContextInner.Provider>
      </Col>
    );
  };

  const finish = () => {
    if (filesValues.some(file => !file.isValid)) {
      return;
    }
    const promises = filesValues.map(file => file.resolve(file.patch));

    Promise.all(promises).then(() => setFiles({}));
  };

  const renderLabels = patch => (
    <>
      {labels?.(patch)}
      {patch?.sigiloso && (
        <ExtraInfo letter={'S'} value={'Arquivo Sigiloso'} color={'warning'} />
      )}
    </>
  );

  return (
    <Modal onClose={askToClose} maxWidth="90%">
      <Panel title={`Enviando ${filesKeys?.length} arquivo(s)`}>
        <Row>
          <Col sm={5} md={5}>
            <TableContainer>
              <Table<FileResolver>
                values={filesValues}
                highlightError={file => !file.isValid}
                keyExtractor={file => file.key}
                tableClassName="word-break"
                rowSelected={file => file.key === selectedFile}
                onClickRow={file => setSelectedFile(file.key)}
                fixed
                footer={
                  <tfoot>
                    <tr>
                      <td colSpan={2}>{dragDropComponent}</td>
                    </tr>
                  </tfoot>
                }
              >
                <Table.Column<FileResolver>
                  header="Arquivo"
                  value={file => (
                    <>
                      {renderLabels(file?.patch || {})}
                      {getUnaccentFileName(getFileName(file.patch, file.file))}
                    </>
                  )}
                />

                <Table.Column<FileResolver>
                  header=""
                  value={file => (
                    <ActionsGroup>
                      {!!file.file?.size && (
                        <ActionButton
                          label="Visualizar"
                          icon="eye"
                          key={`visualizar-button-${file.key}`}
                          onClick={() => previewFile(file.file)}
                        />
                      )}
                      <ActionButton
                        label="Remover"
                        icon="trash"
                        key={`remover-button-${file.key}`}
                        onClick={() => removeFile(file.key)}
                      />
                    </ActionsGroup>
                  )}
                />
              </Table>
            </TableContainer>
          </Col>
          {filesValues.map(renderFile)}
        </Row>
        <Row>
          <Col>
            <Button
              onClick={async () => finish()}
              className="right mr-xs mt-xs"
              disabled={filesValues.some(file => !file.isValid)}
              id="save-files-button"
            >
              Salvar arquivos
            </Button>
          </Col>
        </Row>
      </Panel>
    </Modal>
  );
});
